import { useEffect, useRef, useState } from "react";
import "./PilotButton.css";
import { trackProductEvent } from "@/utils/analytics/track";
import { isDemoUser } from "@/utils/userFlags";
import { useAuth } from "../features/auth";

const BASE_PILOT_QUESTIONS = [
  {
    key: "organization",
    label: "Which organization are you representing?",
    required: true,
    type: "text",
    placeholder: "e.g., Oakland Unified, MLK Elementary, etc.",
  },
  {
    key: "sites",
    label: "How many schools or sites would participate?",
    required: false,
    type: "select",
    options: [
      { value: "", label: "Select one..." },
      { value: "one", label: "One" },
      { value: "multiple", label: "Multiple" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    key: "role",
    label: "What is your role?",
    required: false,
    type: "text",
    placeholder: "e.g., Teacher, Principal, Counselor, Superintendent",
  },
];

// Calendar link - update with actual calendar URL
const CALENDAR_LINK = "https://calendly.com/drew-123wellness"; // Update with actual link

const getQuestions = (formData) => {
  return [...BASE_PILOT_QUESTIONS];
};

const PilotButton = ({ variant = "floating" }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const formStartRef = useRef(null);
  const stepStartRef = useRef(null);
  const abandonTrackedRef = useRef(false);
  const [formData, setFormData] = useState({
    organization: "",
    role: "",
    sites: "",
  });
  const [errors, setErrors] = useState({});
  const [hasStartedTypingRole, setHasStartedTypingRole] = useState(false);

  const isDemo = isDemoUser(user);
  const questions = getQuestions(formData);

  const getDemoOriginContext = () => {
    const signedInAt = sessionStorage.getItem("__demoSignupSignedInAt");
    const completedAt = sessionStorage.getItem("__demoSignupCompletedAt");
    const timestamp = signedInAt || completedAt;
    if (!timestamp) return { demo_origin: false };
    const delayMs = Math.max(0, Date.now() - parseInt(timestamp));
    return { demo_origin: true, delay_ms: delayMs };
  };

  const trackBookingEvent = (eventName, props = {}) => {
    trackProductEvent(
      eventName,
      {
        page: window.location.pathname,
        variant,
        ...getDemoOriginContext(),
        ...props,
      },
      user,
    );
  };

  const getStepMeta = (stepIndex) => {
    const step = questions[stepIndex];
    return {
      step,
      step_key: step?.key || "unknown",
      step_index: stepIndex,
      total_steps: questions.length,
      required: Boolean(step?.required),
      input_type: step?.type || "unknown",
    };
  };

  useEffect(() => {
    if (currentStep >= questions.length) {
      setCurrentStep(Math.max(0, questions.length - 1));
    }
  }, [currentStep, questions.length]);

  useEffect(() => {
    if (!isOpen) return;
    formStartRef.current = performance.now();
    stepStartRef.current = performance.now();
    abandonTrackedRef.current = false;
    const meta = getStepMeta(currentStep);
    trackBookingEvent("demo_booking_open");
    const completedAt = sessionStorage.getItem("__demoSignupCompletedAt");
    const toBookingTracked = sessionStorage.getItem(
      "__demoSignupToBookingTracked",
    );
    if (completedAt && !toBookingTracked) {
      const delayMs = Math.max(0, Date.now() - parseInt(completedAt));
      trackProductEvent(
        "demo_signup_to_booking",
        {
          page: window.location.pathname,
          delay_ms: delayMs,
        },
        user,
      );
      sessionStorage.setItem("__demoSignupToBookingTracked", "true");
    }
    trackBookingEvent("demo_booking_step_view", meta);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const meta = getStepMeta(currentStep);
    trackBookingEvent("demo_booking_step_view", meta);
    stepStartRef.current = performance.now();
  }, [currentStep, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear any errors for this field immediately
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Trigger transition when user starts typing in role field
    if (name === "role" && value.length > 0 && !hasStartedTypingRole) {
      setHasStartedTypingRole(true);
    }

    // Auto-advance only when the select value itself changes (not the "Other" text input)
    if (questions.length > 0) {
      const step = questions[currentStep];
      const isOtherInput = name.endsWith("Other");
      if (
        step &&
        step.type === "select" &&
        name === step.key &&
        !isOtherInput &&
        value &&
        value !== "" &&
        value !== "other"
      ) {
        // Use a longer timeout to ensure state is updated
        setTimeout(() => {
          handleNext();
        }, 200);
      }
    }
  };

  const handleNext = () => {
    if (questions.length === 0) {
      handleSchedule();
      return;
    }

    const step = questions[currentStep];
    if (!step) {
      handleSchedule();
      return;
    }

    // Validate required fields - check if field is empty or just whitespace
    const fieldValue = formData[step.key];
    if (
      step.required &&
      (!fieldValue || (typeof fieldValue === "string" && !fieldValue.trim()))
    ) {
      const meta = getStepMeta(currentStep);
      trackBookingEvent("demo_booking_validation_error", {
        step_key: meta.step_key,
        step_index: meta.step_index,
        error_type: "required",
      });
      setErrors({ [step.key]: "This field is required" });
      return;
    }

    // If "Other" is selected, validate the other text field (only if required)
    if (step.hasOther && formData[step.key] === "other") {
      const otherKey = `${step.key}Other`;
      if (
        step.required &&
        (!formData[otherKey] || !formData[otherKey].trim())
      ) {
        const meta = getStepMeta(currentStep);
        trackBookingEvent("demo_booking_validation_error", {
          step_key: meta.step_key,
          step_index: meta.step_index,
          error_type: "required",
        });
        setErrors({ [otherKey]: "Please provide details" });
        return;
      }
    }

    // Clear all errors before moving forward
    setErrors({});

    const meta = getStepMeta(currentStep);
    const durationMs = Math.max(
      0,
      Math.round(
        performance.now() - (stepStartRef.current || performance.now()),
      ),
    );
    const selectedValue =
      step.type === "select" ? (fieldValue || "").toString() : undefined;
    trackBookingEvent("demo_booking_step_complete", {
      step_key: meta.step_key,
      step_index: meta.step_index,
      duration_ms: durationMs,
      value_length: typeof fieldValue === "string" ? fieldValue.length : 0,
      selected_value: selectedValue,
      required: meta.required,
      input_type: meta.input_type,
    });

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // If on last question, go to schedule
      handleSchedule();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const durationMs = Math.max(
        0,
        Math.round(
          performance.now() - (stepStartRef.current || performance.now()),
        ),
      );
      trackBookingEvent("demo_booking_step_back", {
        from_step: currentStep,
        to_step: currentStep - 1,
        duration_ms: durationMs,
      });
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSchedule = () => {
    const durationMs = Math.max(
      0,
      Math.round(
        performance.now() - (stepStartRef.current || performance.now()),
      ),
    );
    const totalDurationMs = Math.max(
      0,
      Math.round(
        performance.now() - (formStartRef.current || performance.now()),
      ),
    );
    trackBookingEvent("demo_booking_schedule_click", {
      duration_ms: durationMs,
      total_duration_ms: totalDurationMs,
    });
    // Build calendar link with pre-filled information
    // The form data is encoded as URL parameters (a1, a2, a3, etc.) which can be
    // used by calendar booking systems like Calendly to pre-fill custom questions
    const params = new URLSearchParams({
      name: user?.fullName || user?.email || "",
      email: user?.email || "",
      // a1: Organization
      ...(formData.organization && { a1: formData.organization }),
      // a2: Number of schools/sites
      ...(formData.sites && { a2: formData.sites }),
      // a3: Role
      ...(formData.role && { a3: formData.role }),
    });

    // Open calendar booking page in a new tab with all form answers pre-filled
    // Calendly and similar services can read these URL parameters to pre-populate
    // custom questions in their booking forms
    window.open(`${CALENDAR_LINK}?${params.toString()}`, "_blank");
    handleClose("schedule", true);
    // Reset form for next use
    setCurrentStep(0);
    setHasStartedTypingRole(false);
    setFormData({
      organization: "",
      role: "",
      sites: "",
    });
  };

  const handleClose = (reason = "close", completed = false) => {
    if (abandonTrackedRef.current) return;
    const meta = getStepMeta(currentStep);
    const totalDurationMs = Math.max(
      0,
      Math.round(
        performance.now() - (formStartRef.current || performance.now()),
      ),
    );
    const stepDurationMs = Math.max(
      0,
      Math.round(
        performance.now() - (stepStartRef.current || performance.now()),
      ),
    );
    trackBookingEvent("demo_booking_close", {
      reason,
      duration_ms: totalDurationMs,
      completed: Boolean(completed),
    });
    if (!completed) {
      trackBookingEvent("demo_booking_abandon", {
        step_key: meta.step_key,
        step_index: meta.step_index,
        total_duration_ms: totalDurationMs,
        step_duration_ms: stepDurationMs,
      });
    }
    abandonTrackedRef.current = true;
    setIsOpen(false);
  };

  const currentStepData = questions.length > 0 ? questions[currentStep] : null;
  const progress =
    questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  const shouldShow = isDemo;

  if (!shouldShow) {
    return null;
  }

  // Determine which icon row class to use based on user role
  const getIconRowClass = () => {
    const role = user?.role;
    if (role === "student") return "icontextrowstudent";
    if (role === "teacher") return "icontextrowteacher";
    return "icontextrowadmin"; // default for admin, superadmin, 123wellness
  };

  const getIconStyleClass = () => {
    const role = user?.role;
    if (role === "student") return "styleiconstudent";
    if (role === "teacher") return "styleiconteacher";
    return "styleiconadmin"; // default for admin, superadmin, 123wellness
  };

  return (
    <>
      {variant === "floating" ? (
        <button
          className="pilot-button-floating"
          onClick={() => setIsOpen(true)}
          aria-label="Book a demo"
        >
          <span className="pilot-button-icon">🚀</span>
          <span className="pilot-button-text">Book a demo</span>
        </button>
      ) : (
        <div className="menuaction pilot-menuaction">
          <div className={getIconRowClass()} onClick={() => setIsOpen(true)}>
            <div className={getIconStyleClass()}>
              <span className="pilot-icon">🚀</span>
            </div>
            <h1 className="icontext">Book a demo</h1>
          </div>
          <p className="linklabel">Schedule a call with our team</p>
        </div>
      )}

      {isOpen && (
        <div
          className="pilot-modal-overlay"
          onClick={() => handleClose("overlay")}
        >
          <div
            className="pilot-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="pilot-modal-close"
              onClick={() => handleClose("close_button")}
              aria-label="Close"
            >
              ×
            </button>

            <div className="pilot-modal-header">
              <h2>Schedule Your Pilot Call</h2>
              <p>
                Answer a few quick questions (optional) or schedule directly
                below
              </p>
              {questions.length > 0 && (
                <div className="pilot-progress-bar">
                  <div
                    className="pilot-progress-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              )}
            </div>

            <div className="pilot-modal-body">
              {questions.length > 0 && currentStepData ? (
                <div className="pilot-question">
                  <label className="pilot-label">
                    {currentStepData.label}
                    {currentStepData.required && (
                      <span className="pilot-required"> *</span>
                    )}
                  </label>

                  {currentStepData.type === "select" ? (
                    <>
                      <select
                        name={currentStepData.key}
                        value={formData[currentStepData.key]}
                        onChange={handleChange}
                        className={`pilot-input ${errors[currentStepData.key] ? "error" : ""}`}
                      >
                        {currentStepData.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {currentStepData.hasOther &&
                        formData[currentStepData.key] === "other" && (
                          <input
                            type="text"
                            name={`${currentStepData.key}Other`}
                            value={formData[`${currentStepData.key}Other`]}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleNext();
                              }
                            }}
                            className={`pilot-input pilot-input-other ${errors[`${currentStepData.key}Other`] ? "error" : ""}`}
                            placeholder="Please specify..."
                          />
                        )}
                    </>
                  ) : currentStepData.type === "textarea" ? (
                    <textarea
                      name={currentStepData.key}
                      value={formData[currentStepData.key]}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.ctrlKey) {
                          handleNext();
                        }
                      }}
                      className={`pilot-input pilot-textarea ${errors[currentStepData.key] ? "error" : ""}`}
                      placeholder={
                        currentStepData.placeholder ||
                        "Type your message here..."
                      }
                      rows={4}
                    />
                  ) : (
                    <input
                      type={currentStepData.type}
                      name={currentStepData.key}
                      value={formData[currentStepData.key]}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleNext();
                        }
                      }}
                      className={`pilot-input ${errors[currentStepData.key] ? "error" : ""}`}
                      placeholder={
                        currentStepData.placeholder || "Type your answer..."
                      }
                    />
                  )}

                  {errors[currentStepData.key] && (
                    <span className="pilot-error">
                      {errors[currentStepData.key]}
                    </span>
                  )}
                  {currentStepData.hasOther &&
                    formData[currentStepData.key] === "other" &&
                    errors[`${currentStepData.key}Other`] && (
                      <span className="pilot-error">
                        {errors[`${currentStepData.key}Other`]}
                      </span>
                    )}
                </div>
              ) : (
                <div className="pilot-question">
                  <p className="pilot-direct-schedule-text">
                    You can schedule your call directly below, or answer
                    optional questions first.
                  </p>
                </div>
              )}
            </div>

            <div className="pilot-modal-actions">
              <div className="pilot-actions-left">
                {questions.length > 0 && currentStep > 0 && (
                  <button
                    type="button"
                    className="pilot-button-back"
                    onClick={handleBack}
                  >
                    ← Back
                  </button>
                )}
              </div>
              <div
                className={`pilot-actions-right ${currentStep === questions.length - 1 && hasStartedTypingRole ? "pilot-actions-single" : ""}`}
              >
                {/* Show Schedule Call Now after first question and on all questions */}
                {(currentStep >= 1 || questions.length === 0) && (
                  <button
                    type="button"
                    className={`pilot-button-schedule-now ${currentStep === questions.length - 1 && hasStartedTypingRole ? "pilot-button-full-width" : ""}`}
                    onClick={handleSchedule}
                  >
                    Schedule Call Now
                  </button>
                )}
                {/* Show Continue button only if not on last question or if they haven't started typing role */}
                {questions.length > 0 &&
                  (currentStep < questions.length - 1 ||
                    (currentStep === questions.length - 1 &&
                      !hasStartedTypingRole)) && (
                    <button
                      type="button"
                      className={`pilot-button-primary ${currentStep === questions.length - 1 && hasStartedTypingRole ? "pilot-button-hidden" : ""}`}
                      onClick={handleNext}
                    >
                      Continue
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PilotButton;
